'use client';
import React, { useState, useRef, useEffect } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import CheckIcon from '@repo/ui/src/components/Icons/CheckIcon';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';

export interface FilterOption {
  value: string;
  label: string;
  count: number | null;
}

export interface FilterConfig {
  key: string;
  label: string;
  options: Array<FilterOption>;
  singleSelect?: boolean;
  toggle?: boolean;
}

interface FilterChipDropdownProps {
  config: FilterConfig;
  activeValues: Array<string>;
  onFilterToggle: (key: string, value: string) => void;
}

const FilterChipDropdown = ({ config, activeValues, onFilterToggle }: FilterChipDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleFocusOut = (e: FocusEvent) => {
      if (!container.contains(e.relatedTarget as Node)) {
        setIsOpen(false);
      }
    };
    container.addEventListener('focusout', handleFocusOut);
    return () => container.removeEventListener('focusout', handleFocusOut);
  }, []);

  useEffect(() => {
    if (!isOpen || !listboxRef.current) return;
    const selected = listboxRef.current.querySelector<HTMLElement>('[aria-selected="true"]');
    const first = listboxRef.current.querySelector<HTMLElement>('[role="option"]');
    (selected ?? first)?.focus();
  }, [isOpen]);

  const handleOptionClick = (value: string) => onFilterToggle(config.key, value);

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, value: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOptionClick(value);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const options = Array.from(
        listboxRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [],
      );
      const idx = options.indexOf(e.currentTarget);
      const next = e.key === 'ArrowDown' ? options[idx + 1] : options[idx - 1];
      next?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  const activeCount = activeValues.length;

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${config.key}-chip-listbox`}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && isOpen) {
            e.preventDefault();
            setIsOpen(false);
          }
        }}
        className={classNames(
          'flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap',
          activeCount > 0
            ? 'bg-neutral-800 text-white border-neutral-800'
            : 'border-warm-300 text-neutral-700 bg-white hover:bg-warm-100',
        )}
      >
        {config.label}
        {activeCount > 0 && (
          <span className="flex items-center justify-center flex-none w-5 h-5 text-xs font-bold bg-white rounded-full text-neutral-800">
            {activeCount}
          </span>
        )}
        <ChevronIcon direction={isOpen ? 'up' : 'down'} width="12" decorative />
      </button>

      {isOpen && (
        <ul
          ref={listboxRef}
          id={`${config.key}-chip-listbox`}
          role="listbox"
          aria-multiselectable={!config.singleSelect}
          aria-label={config.label}
          className="absolute top-full left-0 mt-1 z-101 bg-white border border-warm-300 rounded shadow-lg min-w-[180px] py-1 max-h-64 overflow-y-auto pl-0 whitespace-nowrap capitalize"
        >
          {config.options.map((opt) => {
            const isSelected = activeValues.includes(opt.value);
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => handleOptionClick(opt.value)}
                onKeyDown={(e) => handleOptionKeyDown(e, opt.value)}
                className={classNames(
                  'flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-warm-100',
                  isSelected ? 'font-medium text-neutral-900' : 'text-neutral-700',
                )}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={classNames(
                      'w-4 h-4 border rounded flex-none flex items-center justify-center',
                      isSelected ? 'bg-neutral-800 border-neutral-800' : 'border-warm-300',
                    )}
                    aria-hidden="true"
                  >
                    {isSelected && <CheckIcon color="white" width="12" decorative />}
                  </span>
                  {opt.label}
                </span>
                {opt.count != null && (
                  <span className="text-xs text-neutral-400 tabular-nums">{opt.count}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FilterChipDropdown;
